import { ChartBarIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getLeadsByDate } from "../../services/api";
import { formatLeadStatus, formatLeadType } from '../../utils/displayFormatters';

const LeadsByDate = ({ dateRangeParam, dateRangeLabel, leadCategory = "", unreadOnly = false }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLeadsByDate = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch data from backend
        console.log('Fetching leads by date data');
        const chartData = await getLeadsByDate(dateRangeParam, '', '', leadCategory, unreadOnly);
        setChartData(chartData);

      } catch (err) {
        setError(err.message);
        console.error('Error fetching leads by date:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeadsByDate();
  }, [dateRangeParam, leadCategory, unreadOnly]);


  // Get unique statuses and types for dynamic bar creation
  const getUniqueKeys = (data, prefix) => {
    const keys = new Set();
    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key.startsWith(prefix)) {
          keys.add(key);
        }
      });
    });
    return Array.from(keys);
  };

  const statusKeys = getUniqueKeys(chartData, 'status_');
  const typeKeys = getUniqueKeys(chartData, 'type_');

  // Color palettes
  const statusColors = {
    'status_new': '#2DD4BF',
    'status_contacted': '#0EA5E9', 
    'status_qualified': '#FBBF24',
    'status_converted': '#34D399',
    'status_lost': '#F87171'
  };

  const typeColors = {
    'type_website': '#3B82F6',
    'type_referral': '#10B981',
    'type_phone': '#F59E0B',
    'type_walk_in': '#EF4444',
    'type_social_media': '#8B5CF6',
    'type_email': '#06B6D4'
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const totalLeads = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      
      return (
        <div className="bg-white p-2 rounded-lg shadow-sm border border-border">
          <p className="font-semibold text-gray-800 text-sm mb-2">{label}</p>
          <p className="text-sm text-gray-600 mb-2">
            Total: {totalLeads} leads
          </p>
          {payload.map((entry, index) => {
            if (entry.value > 0) {
              const keyName = entry.dataKey
                .replace("status_", "")
                .replace("type_", "");
              const displayName = entry.dataKey.startsWith("status_")
                ? formatLeadStatus(keyName).label
                : formatLeadType(keyName);

              return (
                <div key={index} className="flex items-center text-xs">
                  <div
                    className="w-3 h-3 rounded mr-2"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span>
                    {displayName}: {entry.value}
                  </span>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="lg:col-span-6 bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-6 w-6 text-primary mr-2" />
          <h2 className="text-lg font-semibold text-gray-800">Leads by Date</h2>
        </div>
        <div className="text-center py-8">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lg:col-span-6 bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-6 w-6 text-primary mr-2" />
          <h2 className="text-lg font-semibold text-gray-800">Leads by Date</h2>
        </div>
        <div className="text-center py-8 text-red-600">{error}</div>
      </div>
    );
  }

  const handleBarClick = (data, statusKey) => {
    const dateIso = data?.payload?.date_iso;
    if (!dateIso) return;

    const searchParams = new URLSearchParams();
    searchParams.set("start_date", dateIso);
    searchParams.set("end_date", dateIso);

    if (statusKey?.startsWith("status_")) {
      searchParams.set("status", statusKey.replace("status_", ""));
    }

    navigate(`/leads?${searchParams.toString()}`);
  };

  return (
    <div className="lg:col-span-6 bg-white p-6 rounded-xl shadow-sm hover:border hover:border-teal-200 transition duration-300">
      <div className="flex items-center mb-4">
        <ChartBarIcon className="h-6 w-6 text-primary mr-2" />
        <h2 className="text-lg font-semibold text-gray-800">
          Lead Activity by Date {dateRangeLabel ? `(${dateRangeLabel})` : ""}
        </h2>
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: "#666" }}
            tickLine={{ stroke: "#ccc" }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#666" }}
            tickLine={{ stroke: "#ccc" }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: "20px", fontSize: "12px" }}
            iconType="rect"
          />

          {/* Render bars for statuses */}
          {statusKeys.map((statusKey, index) => (
            <Bar
              key={statusKey}
              dataKey={statusKey}
              stackId="status"
              name={formatLeadStatus(statusKey.replace("status_", "")).label}
              fill={statusColors[statusKey] || `hsl(${index * 60}, 70%, 50%)`}
              cursor="pointer"
              onClick={(data) => handleBarClick(data, statusKey)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LeadsByDate;
